import { useEffect, useState, type FormEvent, type ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { api, type PerfilUsuario } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { PrivateLayout } from "../components/PrivateLayout";
import { Reveal } from "../components/Reveal";
import { ConfirmModal } from "../components/ConfirmModal";
import { ToastStack } from "../components/ToastStack";
import { useToast } from "../hooks/useToast";

function Avatar({ url, nombre, size = 84 }: { url: string | null; nombre: string; size?: number }) {
  const initial = (nombre || "U")[0].toUpperCase();
  if (url) {
    return (
      <img src={url} alt={nombre}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover" }} />
    );
  }
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      background: "linear-gradient(135deg, #C99A6E, #9CB89F)",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontSize: size * 0.36, fontWeight: 800, color: "#fff", fontFamily: "var(--font-display)",
    }}>
      {initial}
    </div>
  );
}

export default function Perfil() {
  const navigate = useNavigate();
  const { token, user, clearSession, updateUser } = useAuth();
  const { toasts, showToast, dismiss } = useToast();

  const [perfil,    setPerfil]    = useState<PerfilUsuario | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [nombre,    setNombre]    = useState("");
  const [bio,       setBio]       = useState("");
  const [saving,    setSaving]    = useState(false);
  const [pwActual,  setPwActual]  = useState("");
  const [pwNueva,   setPwNueva]   = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwSaving,  setPwSaving]  = useState(false);
  const [confirmLogout, setConfirmLogout] = useState(false);

  useEffect(() => {
    if (!token) return;
    api.getPerfil(token).then((r) => {
      if (r.ok && r.data.perfil) {
        const p = r.data.perfil;
        setPerfil(p);
        setNombre(p.nombre ?? user?.nombre ?? "");
        setBio(p.bio ?? "");
        updateUser({ foto_url: p.foto_url });
      } else {
        setNombre(user?.nombre ?? "");
      }
      setLoading(false);
    });
  }, [token]);

  async function handleSavePerfil(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    setSaving(true);
    const r = await api.updatePerfil({ nombre: nombre.trim(), bio: bio.trim() }, token);
    setSaving(false);
    if (r.ok) {
      setPerfil(r.data.perfil);
      showToast("Perfil actualizado", "success");
    } else {
      showToast(r.error.message, "error");
    }
  }

  async function handleFotoChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    const input = e.target;
    if (!file || !token) return;

    if (file.size > 4 * 1024 * 1024) {
      showToast("La foto no puede pesar más de 4 MB", "error");
      input.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => {
      showToast("No se pudo leer el archivo seleccionado", "error");
      input.value = "";
    };
    reader.onload = async () => {
      try {
        const dataUrl = reader.result as string;
        const base64 = dataUrl.split(",")[1];
        const r = await api.subirFotoPerfil({ foto_base64: base64, mime_type: file.type }, token);
        if (r.ok) {
          setPerfil((prev) => prev ? { ...prev, foto_url: r.data.foto_url } : prev);
          updateUser({ foto_url: r.data.foto_url });
          showToast("Foto actualizada", "success");
        } else {
          showToast(r.error.message, "error");
        }
      } catch {
        showToast("No se pudo subir la foto. Intenta de nuevo.", "error");
      } finally {
        input.value = "";
      }
    };
    reader.readAsDataURL(file);
  }

  async function handleCambiarPassword(e: FormEvent) {
    e.preventDefault();
    if (!token) return;
    if (pwNueva !== pwConfirm) {
      showToast("Las contraseñas no coinciden", "error");
      return;
    }
    if (pwNueva.length < 8) {
      showToast("La nueva contraseña debe tener al menos 8 caracteres", "error");
      return;
    }
    setPwSaving(true);
    const r = await api.cambiarPassword(
      { password_actual: pwActual, password_nueva: pwNueva }, token
    );
    setPwSaving(false);
    if (r.ok) {
      setPwActual(""); setPwNueva(""); setPwConfirm("");
      showToast("Contraseña cambiada correctamente", "success");
    } else {
      showToast(r.error.message, "error");
    }
  }

  function doLogout() {
    clearSession();
    navigate("/");
  }

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--bg)" }}>
        <span className="spinner spinner--dark" />
      </div>
    );
  }

  return (
    <PrivateLayout maxWidth={760}>
      <Reveal>
        <div style={{ marginBottom: 26 }}>
          <h1 style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 30, letterSpacing: "-0.02em", margin: "0 0 4px" }}>
            Mi perfil
          </h1>
          <p style={{ fontSize: 15, color: "var(--muted-2)", margin: 0 }}>Actualiza tus datos y cómo te ven en la comunidad.</p>
        </div>
      </Reveal>

      {/* photo + name card */}
      <Reveal delay={1}>
        <div className="card" style={{ padding: 26, marginBottom: 22 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 17, marginBottom: 20 }}>Datos personales</div>
          <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 26, flexWrap: "wrap" }}>
            <Avatar url={perfil?.foto_url ?? user?.avatar_url ?? null} nombre={user?.nombre ?? "U"} />
            <div>
              <label className="btn btn-outline btn--sm" style={{ marginBottom: 8, cursor: "pointer" }}>
                Cambiar foto
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={handleFotoChange} />
              </label>
              <div style={{ fontSize: 13, color: "var(--muted-3)" }}>JPG o PNG, máx. 4 MB</div>
            </div>
          </div>

          <form onSubmit={handleSavePerfil}>
            <label className="field-label">Nombre completo</label>
            <input className="form-input" value={nombre} onChange={(e) => setNombre(e.target.value)}
              placeholder="Tu nombre" maxLength={100} style={{ marginBottom: 18 }} />

            <label className="field-label">Email</label>
            <input className="form-input" value={user?.email ?? ""} readOnly
              style={{ opacity: 0.6, cursor: "not-allowed", marginBottom: 18 }} />

            <label className="field-label">Sobre mí <span style={{ fontWeight: 400, color: "var(--muted-3)" }}>({bio.length}/160)</span></label>
            <textarea className="form-input" value={bio} onChange={(e) => setBio(e.target.value.slice(0, 160))}
              placeholder="Cuéntanos algo sobre ti..." rows={3} style={{ marginBottom: 22 }} />

            <button type="submit" className="btn btn-primary btn--full" disabled={saving}>
              {saving ? <><span className="spinner" /> Guardando…</> : "Guardar cambios"}
            </button>
          </form>
        </div>
      </Reveal>

      {/* password card */}
      <Reveal delay={2}>
        <div className="card" style={{ padding: 26, marginBottom: 22 }}>
          <div style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 17, marginBottom: 20 }}>🔒 Cambiar contraseña</div>
          <form onSubmit={handleCambiarPassword}>
            <label className="field-label">Contraseña actual</label>
            <input type="password" className="form-input" value={pwActual} onChange={(e) => setPwActual(e.target.value)}
              placeholder="••••••••" style={{ marginBottom: 18 }} />

            <div className="grid-2" style={{ gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 0 }}>
              <div>
                <label className="field-label">Nueva contraseña</label>
                <input type="password" className="form-input" value={pwNueva} onChange={(e) => setPwNueva(e.target.value)}
                  placeholder="Mínimo 8 caracteres" minLength={8} />
              </div>
              <div>
                <label className="field-label">Confirmar</label>
                <input type="password" className="form-input" value={pwConfirm} onChange={(e) => setPwConfirm(e.target.value)}
                  placeholder="Repite la contraseña" minLength={8} />
              </div>
            </div>

            <button type="submit" className="btn btn-outline btn--full" disabled={pwSaving} style={{ marginTop: 18 }}>
              {pwSaving ? <><span className="spinner" /> Cambiando…</> : "Cambiar contraseña"}
            </button>
          </form>
        </div>
      </Reveal>

      {/* actions */}
      <Reveal delay={3}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <button onClick={() => setConfirmLogout(true)} className="btn-danger-text" style={{ marginLeft: "auto" }}>
            Cerrar sesión
          </button>
        </div>
      </Reveal>

      {confirmLogout && (
        <ConfirmModal
          title="¿Cerrar sesión?"
          message="Puedes volver a entrar cuando quieras. Tu progreso se mantiene guardado."
          confirmLabel="Cerrar sesión"
          danger
          onConfirm={doLogout}
          onCancel={() => setConfirmLogout(false)}
        />
      )}

      <ToastStack toasts={toasts} onDismiss={dismiss} />
    </PrivateLayout>
  );
}
